import DeferredPropsLoopController from '@/types/wayfinder/actions/App/Http/Controllers/Api/Monitoring/DeferredPropsLoopController';
import type { GlobalEvent, Page } from '@inertiajs/core';
import { router } from '@inertiajs/vue3';

// A page load fires one reload per deferred group; some browsers keep re-firing them every response.
const maxReloads = 20;
const windowMs = 10_000;

let reloadTimes: number[] = [];
let isStopped = false;
let currentPage: Page | undefined = undefined;

function isDeferredPropsReload(event: GlobalEvent<'before'>): boolean {
    const visit = event.detail.visit;

    return 'deferredProps' in visit && visit.deferredProps === true;
}

function navigationType(): string | undefined {
    const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];

    return navigation?.type;
}

function historyBytes(): number | undefined {
    const historyPage: unknown = window.history.state?.page;

    return historyPage instanceof ArrayBuffer ? historyPage.byteLength : undefined;
}

function reportLoop(): void {
    if (typeof navigator.sendBeacon !== 'function') {
        return;
    }

    navigator.sendBeacon(
        DeferredPropsLoopController.url({
            query: { component: currentPage?.component, navigation: navigationType(), history_bytes: historyBytes() },
        }),
    );
}

function onBeforeVisit(event: GlobalEvent<'before'>): boolean | void {
    if (!isDeferredPropsReload(event)) {
        return;
    }

    if (isStopped) {
        return false;
    }

    const now = Date.now();
    reloadTimes = [...reloadTimes.filter((time: number) => now - time < windowMs), now];

    if (reloadTimes.length <= maxReloads) {
        return;
    }

    isStopped = true;
    reportLoop();

    return false;
}

// Inertia also fires navigate for popstate and for a reload response whose URL differs from the address bar.
function onNavigate(event: GlobalEvent<'navigate'>): void {
    const page = event.detail.page;

    if (page.component === currentPage?.component && page.url === currentPage?.url) {
        return;
    }

    currentPage = page;
    reloadTimes = [];
    isStopped = false;
}

/** Cancels a page's deferred props reloads once they loop, and reports it once per page. */
export function registerDeferredPropsLoopGuard(): void {
    router.on('navigate', onNavigate);
    router.on('before', onBeforeVisit);
}
